import {
  ArchiveRestoreIcon,
  ArchiveXIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  GripVerticalIcon,
  LayersIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
} from 'lucide-react'
import { useState, type DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  updateCategory,
  updateInterest,
  type ProductCategory,
  type ProductInterest,
} from '@/api/productCatalog'
import { useApiErrorMessage } from '@/api/errors'
import { Role } from '@/auth/roles'
import { useAuth } from '@/auth/useAuth'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { CategoryDialog } from '@/components/catalog/CategoryDialog'
import { InterestDialog } from '@/components/catalog/InterestDialog'
import { moveBy, moveTo, renumbered } from '@/components/catalog/ordering'
import { searchCatalog } from '@/components/catalog/search'
import { PageHeader } from '@/components/layout/PageHeader'
import { useProductCatalog } from '@/hooks/useProductCatalog'

/**
 * The two-level catalogue leads are tagged with. Nothing here deletes: retiring an entry takes it
 * out of the picker and leaves the leads that reference it readable, which is why the actions are
 * "retire" and "restore" rather than a bin.
 *
 * Administrators also set the order the categories are offered in, because that order is a
 * business decision rather than an alphabet — see `ordering.ts`. Two ways in, and they are not
 * decoration of each other: dragging is what somebody reaches for with a mouse, and the two menu
 * entries are the same move for a keyboard and for a screen reader, which cannot drag anything.
 */
/**
 * What a drag is carrying: the row's id, and the list it belongs to — the card for an interest,
 * `null` for a category, which sits in the outer list. Two drags meet only when they are
 * siblings.
 */
type Drag = { id: string; parentId: string | null }

export function ProductCatalogPage() {
  const { t } = useTranslation()
  const { hasRole } = useAuth()
  const errorMessage = useApiErrorMessage()
  const canEdit = hasRole(Role.ADMIN)
  // An administrator on this page is here to maintain it, so the retired entries start visible.
  const catalog = useProductCatalog(canEdit)

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null)
  const [interestDialogOpen, setInterestDialogOpen] = useState(false)
  const [editingInterest, setEditingInterest] = useState<ProductInterest | null>(null)
  const [interestCategoryId, setInterestCategoryId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /**
   * The order a move has just produced, shown straight away rather than after the round trip —
   * dragging a card and watching it spring back while the request is in flight is what makes a
   * list feel broken.
   *
   * Tied to the array it was computed from, so it retires itself: once the reload brings a new
   * list, `source` no longer matches and the server's order is what renders.
   */
  const [moved, setMoved] = useState<{ source: ProductCategory[]; order: ProductCategory[] } | null>(
    null,
  )
  /**
   * What is being dragged, and the row it is currently over.
   *
   * Which list it came from is carried along rather than inferred, because the two are nested: an
   * interest row sits inside the card of its category, so every drag event over it also reaches
   * the card. This is what lets each level ignore the other's drag instead of both claiming it.
   */
  const [dragging, setDragging] = useState<Drag | null>(null)
  const [over, setOver] = useState<string | null>(null)
  /**
   * Set while the pointer is on a drag handle, which is the only thing that makes a row
   * draggable. Without it the whole card is a drag source and selecting a category's name with
   * the mouse drags the card instead.
   */
  const [grabbed, setGrabbed] = useState<string | null>(null)

  /**
   * In the address bar, as on every other list screen, so a reload lands back on what was being
   * looked at. No debounce and no request behind it: the catalogue is already here in full, and
   * the filtering is a pass over a few dozen names.
   */
  const [params, setParams] = useSearchParams()

  /** One parameter, written in place. Empty removes it, so a default never shows in the URL. */
  function writeParam(key: string, value: string) {
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (value === '') {
          updated.delete(key)
        } else {
          updated.set(key, value)
        }
        return updated
      },
      // Replaced rather than pushed: typing is not somewhere to navigate back through, and nor is
      // folding a card away.
      { replace: true },
    )
  }

  const search = params.get('q') ?? ''
  const setSearch = (next: string) => writeParam('q', next)

  /**
   * The cards that are unfolded — the *open* ones, because every plant starts folded away: six
   * plants and forty-five products is more than a screenful, and most visits are about one of
   * them. So the ordinary state of this page carries no parameter at all and a link to it stays
   * short.
   */
  const expanded = new Set((params.get('open') ?? '').split(',').filter(Boolean))

  function setOpen(id: string, open: boolean) {
    const next = new Set(expanded)
    if (open) {
      next.add(id)
    } else {
      next.delete(id)
    }
    writeParam('open', [...next].join(','))
  }

  const categories =
    moved && moved.source === catalog.categories ? moved.order : catalog.categories
  const found = searchCatalog(categories, search)

  /**
   * Reordering needs the whole list in view. A search hides categories, and hiding the retired
   * ones hides positions — in either case the neighbour on screen is not the neighbour in the
   * catalogue, and "one place up" would land somewhere nobody pointed at.
   */
  const canReorder = canEdit && search === '' && catalog.includeInactive

  /**
   * Whether a move may start right now: nothing being written, and no reload on its way.
   *
   * Deliberately separate from `canReorder`, which is about the *view* — that one also decides
   * whether the grips and the chevron are rendered at all and whether the hint below the toolbar
   * shows. Folding "a write is in flight" into it would make the grips vanish and the hint flash
   * on every single save.
   *
   * The reload half matters as much as the write half: a second move started before the first
   * one's reload has landed is bound to a `catalog.categories` that is about to be replaced, and
   * the list visibly springs back to the first move's result until the second reload arrives.
   */
  const settled = !busy && !catalog.loading

  /**
   * What the one button up in the toolbar offers. Anything still open means "fold it all away";
   * only when every card is already folded does it become the way back.
   */
  const allClosed = categories.length > 0 && categories.every((one) => !expanded.has(one.id))

  async function toggle(label: string, action: () => Promise<unknown>) {
    if (busy) {
      return
    }
    setBusy(true)
    try {
      await action()
      toast.success(label)
      catalog.reload()
    } catch (error) {
      toast.error(errorMessage(error, t('catalog.toggleFailed')))
    } finally {
      setBusy(false)
    }
  }

  /**
   * A new order on screen, and the writes that make it stick.
   *
   * There is no endpoint for a whole list, so a move is a handful of ordinary updates — only for
   * the entries that actually changed place, each given its position in the list, so the stored
   * order comes out gapless. Sequentially rather than in parallel: half a dozen writes racing on
   * the same table is not worth the tenth of a second.
   *
   * The reload runs whether or not they all went through. After a failure the screen then shows
   * what the server really holds rather than the order somebody was promised.
   */
  async function apply(order: ProductCategory[], writes: (() => Promise<unknown>)[], done: string, failed: string) {
    // The last line of defence for every way in — the handles, the drop targets and the menu
    // entries. The `draggable` flag alone would leave a drop from somewhere else free to
    // rearrange a list this account is only allowed to read.
    if (!canReorder || !settled || writes.length === 0) {
      return
    }
    setBusy(true)
    setMoved({ source: catalog.categories, order })
    try {
      for (const write of writes) {
        await write()
      }
      toast.success(done)
    } catch (error) {
      toast.error(errorMessage(error, failed))
    } finally {
      setBusy(false)
      catalog.reload()
    }
  }

  function reorderCategories(next: ProductCategory[]) {
    void apply(
      next,
      renumbered(next).map(
        ({ entry, sortIndex }) =>
          () =>
            updateCategory(entry.id, { name: entry.name, sortIndex, active: entry.active }),
      ),
      t('catalog.category.reordered'),
      t('catalog.category.reorderFailed'),
    )
  }

  /**
   * The same, one level down. `categoryId` goes back out unchanged: on this endpoint it doubles
   * as the move between categories, and sending anything else here would relocate the interest
   * rather than reorder it — that move belongs to the dialog.
   */
  function reorderInterests(category: ProductCategory, next: ProductInterest[]) {
    void apply(
      categories.map((one) => (one.id === category.id ? { ...one, interests: next } : one)),
      renumbered(next).map(
        ({ entry, sortIndex }) =>
          () =>
            updateInterest(entry.id, {
              categoryId: entry.categoryId,
              name: entry.name,
              sortIndex,
              active: entry.active,
            }),
      ),
      t('catalog.interest.reordered'),
      t('catalog.interest.reorderFailed'),
    )
  }

  /** How a row looks during a drag: lit up as the drop target, faded as the one being moved. */
  function dragClass(id: string): string | undefined {
    if (over === id) {
      return 'ring-primary ring-2'
    }
    return dragging?.id === id ? 'opacity-50' : undefined
  }

  /** Ends the gesture, whichever level it was on. */
  function endDrag() {
    setDragging(null)
    setOver(null)
    setGrabbed(null)
  }

  function startDrag(event: DragEvent, drag: Drag) {
    // An interest row sits inside its category's card, so without this the card would read the
    // same dragstart as its own and both levels would think they are moving.
    event.stopPropagation()
    setDragging(drag)
    event.dataTransfer.effectAllowed = 'move'
    // Firefox starts no drag at all without something on the transfer.
    event.dataTransfer.setData('text/plain', drag.id)
  }

  /**
   * Whether a drag hovering over `target` may be dropped there: only between siblings.
   *
   * Anything else is simply not answered — no `preventDefault` means "not a drop target", so the
   * event goes on bubbling and the level it belongs to gets it. That is what keeps a category
   * drag from landing inside an interest list, and an interest inside its own category:
   * relocating one to another category is the move the dialog owns, and doing it silently by
   * drag is not what somebody rearranging a list means.
   */
  function accepts(drag: Drag | null, target: Drag): drag is Drag {
    return drag !== null && drag.id !== target.id && drag.parentId === target.parentId
  }

  function dragOver(event: DragEvent, target: Drag) {
    // `settled` as well as `canReorder`: a drop target that lights up while the previous move is
    // still being written promises something the guard in `apply` would then quietly refuse.
    if (!canReorder || !settled || !accepts(dragging, target)) {
      return
    }
    // Without this the drop never fires: the default for a dragover is "not a drop target".
    event.preventDefault()
    // Only the level that accepted it — otherwise the card behind an interest row lights up too.
    event.stopPropagation()
    event.dataTransfer.dropEffect = 'move'
    setOver(target.id)
  }

  function drop(event: DragEvent, target: Drag, category: ProductCategory) {
    const source = dragging
    if (!accepts(source, target)) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    endDrag()
    if (source.parentId === null) {
      reorderCategories(moveTo(categories, source.id, target.id))
    } else {
      reorderInterests(category, moveTo(category.interests, source.id, target.id))
    }
  }

  function openCategory(category: ProductCategory | null) {
    setEditingCategory(category)
    setCategoryDialogOpen(true)
  }

  function openInterest(interest: ProductInterest | null, categoryId: string) {
    setEditingInterest(interest)
    setInterestCategoryId(categoryId)
    setInterestDialogOpen(true)
  }

  return (
    <>
      <PageHeader title={t('catalog.title')}>
        {canEdit && (
          <Button onClick={() => openCategory(null)}>
            <PlusIcon />
            {t('catalog.category.create')}
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <SearchIcon
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            type="search"
            className="pl-8"
            aria-label={t('catalog.searchLabel')}
            placeholder={t('catalog.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="include-inactive"
            checked={catalog.includeInactive}
            onCheckedChange={(checked) => catalog.setIncludeInactive(checked === true)}
          />
          <Label htmlFor="include-inactive" className="text-sm font-normal">
            {t('catalog.includeInactive')}
          </Label>
        </div>
        {/* One press for the whole page, because unfolding six plants one at a time is six
            presses to see them all. Disabled while a search is narrowing the list — the search
            opens what it finds, like it overrides the chevrons — and when there is nothing to
            fold. Disabled rather than gone, so typing the first letter of a search does not shove
            the toolbar sideways or leave a hole in it. */}
        <Button
          variant="outline"
          size="sm"
          disabled={search !== '' || categories.length === 0}
          onClick={() =>
            writeParam('open', allClosed ? categories.map((one) => one.id).join(',') : '')
          }
        >
          {allClosed ? <ChevronsUpDownIcon /> : <ChevronsDownUpIcon />}
          {allClosed ? t('catalog.expandAll') : t('catalog.collapseAll')}
        </Button>
        <Button
          variant="outline"
          size="icon"
          aria-label={t('catalog.reload')}
          onClick={catalog.reload}
        >
          <RefreshCwIcon />
        </Button>
      </div>

      {/* Says why the handles and the two menu entries are gone, rather than leaving an
          administrator to work out that the search is what took them away. */}
      {canEdit && !canReorder && (
        <p className="text-muted-foreground text-xs">{t('catalog.reorderHint')}</p>
      )}

      {catalog.error ? (
        <Alert variant="destructive">
          <AlertTitle>{t('catalog.loadFailed')}</AlertTitle>
          <AlertDescription>
            {errorMessage(catalog.error, t('common.unexpectedError'))}
          </AlertDescription>
          <AlertAction>
            <Button variant="outline" size="sm" onClick={catalog.reload}>
              {t('catalog.retry')}
            </Button>
          </AlertAction>
        </Alert>
      ) : catalog.loading && catalog.categories.length === 0 ? (
        <div className="grid gap-4">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-40 w-full" />
          ))}
        </div>
      ) : found.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground flex flex-col items-center gap-2 py-12 text-center">
            <LayersIcon className="size-6" aria-hidden />
            <p className="text-sm">
              {search ? t('catalog.emptySearch', { search }) : t('catalog.empty')}
            </p>
            {search && (
              <>
                <p className="text-xs">{t('catalog.emptySearchHint')}</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setSearch('')}>
                  {t('common.clearFilters')}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {found.map(({ category, interests, total }) => {
            // The position in the catalogue, not in what the search left: the ends of the list are
            // where "up" and "down" stop, and the search does not move them.
            const position = categories.indexOf(category)
            const drag: Drag = { id: category.id, parentId: null }
            // A search that narrowed this card from the inside has to show what it found, so a
            // card folded away earlier opens for as long as the search lasts — and the chevron
            // steps aside rather than becoming a control that does nothing.
            const open = search !== '' || expanded.has(category.id)

            return (
              <Card
                key={category.id}
                data-testid={`category-${category.id}`}
                // Only while a handle is held. A card that is always draggable turns selecting its
                // name with the mouse into a drag.
                draggable={canReorder && settled && grabbed === category.id}
                onDragStart={(event) => startDrag(event, drag)}
                onDragEnd={endDrag}
                onDragOver={(event) => dragOver(event, drag)}
                onDragLeave={() => setOver((current) => (current === category.id ? null : current))}
                onDrop={(event) => drop(event, drag, category)}
                className={dragClass(category.id)}
              >
                {/* The card's own `flex flex-col gap` is repeated here, because the collapsible
                    now sits between it and the two children it was spacing apart. */}
                <Collapsible
                  open={open}
                  onOpenChange={(next) => setOpen(category.id, next)}
                  className="flex flex-col gap-(--card-spacing)"
                >
                  <CardHeader className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-2">
                      {canReorder && (
                        /* A grip, not the whole card: see `draggable` above. It is a span rather than
                           a button because dragging is its only job — the keyboard equivalent is in
                           the menu, where it can be reached and read out. */
                        <span
                          aria-hidden
                          title={t('catalog.dragHandle', { name: category.name })}
                          className="text-muted-foreground mt-0.5 cursor-grab active:cursor-grabbing"
                          onPointerDown={() => setGrabbed(category.id)}
                          onPointerUp={() => setGrabbed(null)}
                        >
                          <GripVerticalIcon className="size-4" />
                        </span>
                      )}
                      {search === '' && (
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="-ml-1 shrink-0"
                            aria-label={t('catalog.category.toggle', { name: category.name })}
                          >
                            {/* Turned rather than swapped: the same shape rotating is what says the
                                two states are one control. */}
                            <ChevronDownIcon
                              className={open ? 'transition-transform' : '-rotate-90 transition-transform'}
                            />
                          </Button>
                        </CollapsibleTrigger>
                      )}
                    <div className="space-y-1">
                      <CardTitle className="flex items-center gap-2">
                        {category.name}
                        {!category.active && (
                          <Badge variant="outline" className="font-normal">
                            {t('catalog.retired')}
                          </Badge>
                        )}
                      </CardTitle>
                      <p className="text-muted-foreground text-sm">
                        {/* While a search is narrowing a category from the inside, the card would
                            otherwise claim the category only ever had the interests still showing. */}
                        {interests.length === total
                          ? t('catalog.interestCount', { count: total })
                          : t('catalog.interestCountFiltered', { count: total, shown: interests.length })}
                      </p>
                    </div>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openInterest(null, category.id)}
                        >
                          <PlusIcon />
                          {t('catalog.interest.create')}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={t('catalog.category.menu', { name: category.name })}
                            >
                              <MoreHorizontalIcon />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem onSelect={() => openCategory(category)}>
                              <PencilIcon />
                              {t('catalog.actions.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {/* The same move as dragging, for everybody who cannot drag. Kept in the
                                menu rather than shown as two arrows per card: the order is set once
                                and then left alone, and it is not what this page is for. */}
                            <DropdownMenuItem
                              disabled={!canReorder || !settled || position <= 0}
                              onSelect={() => reorderCategories(moveBy(categories, category.id, -1))}
                            >
                              <ArrowUpIcon />
                              {t('catalog.actions.moveUp')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={!canReorder || !settled || position >= categories.length - 1}
                              onSelect={() => reorderCategories(moveBy(categories, category.id, 1))}
                            >
                              <ArrowDownIcon />
                              {t('catalog.actions.moveDown')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={() =>
                                void toggle(
                                  category.active
                                    ? t('catalog.category.retired', { name: category.name })
                                    : t('catalog.category.restored', { name: category.name }),
                                  () =>
                                    updateCategory(category.id, {
                                      name: category.name,
                                      active: !category.active,
                                    }),
                                )
                              }
                            >
                              {category.active ? <ArchiveXIcon /> : <ArchiveRestoreIcon />}
                              {category.active ? t('catalog.actions.retire') : t('catalog.actions.restore')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </CardHeader>

                  <CollapsibleContent>
                    <CardContent>
                      {interests.length === 0 ? (
                        <p className="text-muted-foreground text-sm">{t('catalog.noInterests')}</p>
                      ) : (
                        <ul className="divide-y rounded-lg border">
                          {interests.map((interest) => {
                            // Within the category, not within what the search left over — the ends of
                            // this list are where "up" and "down" stop.
                            const at = category.interests.indexOf(interest)
                            const row: Drag = { id: interest.id, parentId: category.id }

                            return (
                              <li
                                key={interest.id}
                                className={`flex items-center justify-between gap-3 px-3 py-2 ${dragClass(interest.id) ?? ''}`}
                                data-testid={`interest-${interest.id}`}
                                draggable={canReorder && settled && grabbed === interest.id}
                                onDragStart={(event) => startDrag(event, row)}
                                onDragEnd={endDrag}
                                onDragOver={(event) => dragOver(event, row)}
                                onDragLeave={() =>
                                  setOver((current) => (current === interest.id ? null : current))
                                }
                                onDrop={(event) => drop(event, row, category)}
                              >
                                <span className="flex min-w-0 items-center gap-2 text-sm">
                                  {canReorder && (
                                    /* The same grip as on a card, and for the same reason: without it
                                       the row is a drag source and selecting its name starts a drag. */
                                    <span
                                      aria-hidden
                                      title={t('catalog.dragHandle', { name: interest.name })}
                                      className="text-muted-foreground cursor-grab active:cursor-grabbing"
                                      onPointerDown={() => setGrabbed(interest.id)}
                                      onPointerUp={() => setGrabbed(null)}
                                    >
                                      <GripVerticalIcon className="size-4" />
                                    </span>
                                  )}
                                  {interest.name}
                                  {!interest.active && (
                                    <Badge variant="outline" className="font-normal">
                                      {t('catalog.retired')}
                                    </Badge>
                                  )}
                                </span>
                                {canEdit && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        aria-label={t('catalog.interest.menu', { name: interest.name })}
                                      >
                                        <MoreHorizontalIcon />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                      <DropdownMenuItem
                                        onSelect={() => openInterest(interest, interest.categoryId)}
                                      >
                                        <PencilIcon />
                                        {t('catalog.actions.edit')}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      {/* The same move as dragging, for everybody who cannot drag. */}
                                      <DropdownMenuItem
                                        disabled={!canReorder || !settled || at <= 0}
                                        onSelect={() =>
                                          reorderInterests(
                                            category,
                                            moveBy(category.interests, interest.id, -1),
                                          )
                                        }
                                      >
                                        <ArrowUpIcon />
                                        {t('catalog.actions.moveUp')}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        disabled={
                                          !canReorder || !settled || at >= category.interests.length - 1
                                        }
                                        onSelect={() =>
                                          reorderInterests(
                                            category,
                                            moveBy(category.interests, interest.id, 1),
                                          )
                                        }
                                      >
                                        <ArrowDownIcon />
                                        {t('catalog.actions.moveDown')}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onSelect={() =>
                                          void toggle(
                                            interest.active
                                              ? t('catalog.interest.retired', { name: interest.name })
                                              : t('catalog.interest.restored', { name: interest.name }),
                                            () =>
                                              updateInterest(interest.id, {
                                                categoryId: interest.categoryId,
                                                name: interest.name,
                                                active: !interest.active,
                                              }),
                                          )
                                        }
                                      >
                                        {interest.active ? <ArchiveXIcon /> : <ArchiveRestoreIcon />}
                                        {interest.active
                                          ? t('catalog.actions.retire')
                                          : t('catalog.actions.restore')}
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            )
          })}
        </div>
      )}

      <CategoryDialog
        open={categoryDialogOpen}
        category={editingCategory}
        onOpenChange={setCategoryDialogOpen}
        onSaved={catalog.reload}
      />
      <InterestDialog
        open={interestDialogOpen}
        interest={editingInterest}
        categories={catalog.categories}
        defaultCategoryId={interestCategoryId}
        onOpenChange={setInterestDialogOpen}
        onSaved={catalog.reload}
      />
    </>
  )
}
